import React, { Fragment, ReactNode } from 'react';
import { PlayerResult, RoomResult } from '@shared/api-dto';
import { AwardId, getGameAwards } from '@shared/awards';
import { getPlayerByGuid } from './room-data-helpers';

import './game-awards.css';

const AwardIcon = ({ id }: { id: AwardId }) => {
  if (id === 'mindReader') {
    return (
      <svg viewBox='0 0 24 24' aria-hidden='true'>
        <path d='M12 5.2c4.8 0 8.7 2.9 10.2 6.8-1.5 3.9-5.4 6.8-10.2 6.8S3.3 15.9 1.8 12C3.3 8.1 7.2 5.2 12 5.2zm0 3.3a3.5 3.5 0 100 7 3.5 3.5 0 000-7z' />
      </svg>
    );
  }
  if (id === 'biggestOutlier') {
    return (
      <svg viewBox='0 0 24 24' aria-hidden='true'>
        <circle cx='6.2' cy='12.2' r='2.15' />
        <circle cx='10.6' cy='12.2' r='2.15' />
        <circle cx='18.4' cy='12.2' r='2.9' />
      </svg>
    );
  }
  if (id === 'crowdFavorite') {
    return (
      <svg viewBox='0 0 24 24' aria-hidden='true'>
        <path d='M12 3.4l2.35 6.55H21.3l-5.4 3.95 2.1 6.6L12 16.7 5.99 20.5l2.1-6.6-5.4-3.95h6.95z' />
      </svg>
    );
  }
  return (
    <svg viewBox='0 0 24 24' aria-hidden='true'>
      <path d='M12 3.4a8.6 8.6 0 100 17.2 8.6 8.6 0 000-17.2zm-1.15 12.05L6.7 11.3l1.55-1.55 2.55 2.55 4.9-5.05 1.6 1.55-6.5 7.05z' />
    </svg>
  );
};

const joinWinners = (players: PlayerResult[]): ReactNode => (
  players.map((p, i) => {
    const sep = i === 0 ? null : i === players.length - 1 ? ' and ' : ', ';
    const teamClass = p.team === 1 ? 'team1' : p.team === 2 ? 'team2' : '';
    return (
      <Fragment key={p.user.guid}>
        {sep}
        <span className={'awardWinner' + (teamClass ? ` ${teamClass}` : '')}>
          {p.user.name}
        </span>
      </Fragment>
    );
  })
);

type GameAwardsProps = {
  roomData: RoomResult;
};

export const GameAwards = ({ roomData }: GameAwardsProps) => {
  const awards = getGameAwards(roomData)
    .map(award => ({
      ...award,
      winners: award.winnerGuids
        .map(guid => getPlayerByGuid(roomData.players, guid))
        .filter((p): p is PlayerResult => Boolean(p)),
    }))
    .filter(award => award.winners.length > 0);

  if (awards.length === 0) {
    return null;
  }

  return (
    <div className='gameAwards'>
      <div className='gameAwardsHeading'>Player awards</div>
      <div className='gameAwardsList'>
        {awards.map(award => (
          <div key={award.id} className='gameAward' data-award={award.id}>
            <span className='awardIcon'>
              <AwardIcon id={award.id} />
            </span>
            <span className='awardCopy'>
              <span className='awardTitle'>{award.title}</span>
              <span className='awardWinners'>{joinWinners(award.winners)}</span>
              <span className='awardReason'>{award.reason}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
